import {
  Dropdown,
  SearchEngineType,
  type SetDropdownStateRef,
} from "@hoylu/client-common";
import DefaultDropdownStyle from "@hoylu/client-common/dist/assets/css.modules/default.dropdown.module.css";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { selectCanAssignProjectToWorkspace } from "../../selectors/selectCanAssignProjectToWorkspace.selector";
import type { AppState } from "../../state/root.reducer";
import { createSelectCanRemoveProjectFromWorkspace } from "../../state/workspaces/projects.selector";
import { activateDashboardOption } from "../../state/workspaces/workspaces.actions";
import { selectSelectedWorkspace } from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import { useSingleSelectDropdownTranslation } from "../../utils/hooks/useSingleSelectDropdownTranslation";
import { RemoveButton } from "../modals/modalElements/RemoveButton";
import Styles from "./ProjectDropdownWithRemoveButton.module.css";
import { ProjectOptionRenderer } from "./ProjectOptionRenderer";
import { ProjectValueRenderer } from "./ProjectValueRenderer";
import type { UseAssignWorkspaceToProjectResult } from "./types";

type ProjectDropdownWithRemoveButton = {
  isEditing: boolean;
  setDropdownStateRef: React.Ref<SetDropdownStateRef>;
} & Omit<
  UseAssignWorkspaceToProjectResult,
  "projectHasChanged" | "saveProject"
>;

export const ProjectDropdownWithRemoveButton = ({
  currentProjects,
  isEditing,
  projectOptions,
  setCurrentProjects,
  setDropdownStateRef,
}: ProjectDropdownWithRemoveButton) => {
  const dispatch = useDispatch();
  const t = useI18n("SHARE_WORKSPACE_DIALOG.");
  const dropdownTranslation = useSingleSelectDropdownTranslation();

  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace);
  const canAssignProject = useSelector((state: AppState) =>
    selectCanAssignProjectToWorkspace(state, selectedMyWorkspaceMetadata),
  );
  const canRemoveProjectFromWorkspace = useMemo(
    createSelectCanRemoveProjectFromWorkspace,
    [],
  );
  const canRemoveProject = useSelector((state: RootState) =>
    canRemoveProjectFromWorkspace(
      state,
      selectedMyWorkspaceMetadata?.containerId,
    ),
  );

  const dropdownClasses = useMemo(() => {
    return [
      DefaultDropdownStyle.dropdown,
      Styles.fixEmptyProjectPosition,
      Styles.dropdown,
    ];
  }, []);

  return (
    <div className={Styles.projectDropdownContainer}>
      <Dropdown
        allowReset={false}
        classes={dropdownClasses}
        localizationDict={dropdownTranslation}
        onChange={setCurrentProjects}
        optionRenderer={ProjectOptionRenderer}
        options={projectOptions}
        readonly={!isEditing || !canAssignProject}
        searchEngineType={SearchEngineType.FUZZY}
        setDropdownStateRef={setDropdownStateRef}
        showLabel={true}
        title={t("PROJECT")}
        valueRenderer={ProjectValueRenderer}
        values={currentProjects}
      />
      {canRemoveProject && (
        <RemoveButton
          disabled={!isEditing}
          onClick={() =>
            dispatch(
              activateDashboardOption({
                optionType: DashboardOption.REMOVE_FROM_PROJECT,
                workspaceId: selectedMyWorkspaceMetadata?.workspaceId,
              }),
            )
          }
          text={t("REMOVE_FROM_PROJECT")}
        />
      )}
    </div>
  );
};
